import type { AppContext } from '../app/AppContext';
import type { SceneManager } from '../app/SceneManager';
import { GameName, RoomName } from '../net/enums';
import { gameState } from '../state/GameState';
import { CreateGameScene } from '../scenes/CreateGameScene';
import { LoginScene } from '../scenes/LoginScene';
import { OnlineListScene } from '../scenes/OnlineListScene';
import { PaymentScene } from '../scenes/PaymentScene';
import { ProfileScene } from '../scenes/ProfileScene';
import { ReconnectScene } from '../scenes/ReconnectScene';
import { RegisterScene } from '../scenes/RegisterScene';
import { SearchGameScene } from '../scenes/SearchGameScene';
import { TicketScene } from '../scenes/TicketScene';
import type { VisualFixtureSlug, VisualFixtureTransport } from './fixtureTransport';

export interface VisualFixtureReadyState {
  ready: true;
  slug: VisualFixtureSlug;
}

declare global {
  interface Window {
    __PARDIS_VISUAL_READY__?: VisualFixtureReadyState;
  }
}

/**
 * Render one deterministic pre-game page and publish the capture contract after its real protocol
 * responses, fonts, DOM overlays, and Pixi render have settled.
 */
export async function showVisualFixture(
  slug: VisualFixtureSlug,
  transport: VisualFixtureTransport,
  ctx: AppContext,
  scenes: SceneManager,
): Promise<void> {
  delete window.__PARDIS_VISUAL_READY__;
  document.documentElement.dataset.visualFixture = slug;

  gameState.reset();
  if (!slug.startsWith('login') && !slug.startsWith('register')) {
    gameState.update({
      connected: true,
      userId: 900_724n,
      username: 'سارا_آس',
      avatar: 7,
      balance: 12_750_000n,
      onlineCount: 128,
      isWaiting: false,
      reconnectToken: 'visual-fixture-reconnect-token',
      adminMessage: '',
    });
  }

  switch (slug) {
    case 'login':
    case 'login-forgot': {
      const scene = new LoginScene(ctx, { useStoredCredentials: false });
      scenes.show(scene);
      if (slug === 'login-forgot') scene.openForgotPassword();
      break;
    }
    case 'register-referral':
    case 'register-form':
      scenes.show(new RegisterScene(ctx));
      if (slug === 'register-form') transport.revealRegisterForm();
      break;
    case 'main-menu':
    case 'main-menu-waiting':
      ctx.navigate(RoomName.MainMenu);
      break;
    case 'online-list':
      scenes.show(new OnlineListScene(ctx));
      break;
    case 'profile':
      scenes.show(new ProfileScene(ctx));
      break;
    case 'payment':
      scenes.show(new PaymentScene(ctx));
      break;
    case 'ticket-new':
    case 'ticket-detail':
      scenes.show(new TicketScene(ctx));
      break;
    case 'create-blackjack':
      scenes.show(new CreateGameScene(ctx, GameName.Blackjack));
      break;
    case 'create-hokm':
      scenes.show(new CreateGameScene(ctx, GameName.Hokm));
      break;
    case 'create-backgammon':
      scenes.show(new CreateGameScene(ctx, GameName.Backgammon));
      break;
    case 'search-empty':
    case 'search-populated':
      scenes.show(new SearchGameScene(ctx));
      break;
    case 'ready-owner':
      transport.showReady(true);
      break;
    case 'ready-joiner':
      transport.showReady(false);
      break;
    case 'reconnect':
      scenes.show(new ReconnectScene(ctx));
      break;
    default: {
      const neverSlug: never = slug;
      throw new Error(`Unsupported visual fixture: ${neverSlug}`);
    }
  }

  // Cash triggers an AgentList request from inside its Cash response handler; waitForIdle tracks the
  // nested frame as well, so the ready flag never races the referred-user count repaint.
  await transport.waitForIdle();
  await document.fonts.ready;
  document.getElementById('boot-shell')?.remove();
  await nextFrame();
  await nextFrame();
  window.__PARDIS_VISUAL_READY__ = { ready: true, slug };
  document.documentElement.dataset.visualFixtureReady = 'true';
}

function nextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}
